import type { SvgIconComponent } from '@mui/icons-material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import LoyaltyRoundedIcon from '@mui/icons-material/LoyaltyRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded';
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import SmartDisplayRoundedIcon from '@mui/icons-material/SmartDisplayRounded';
import SpaceDashboardRoundedIcon from '@mui/icons-material/SpaceDashboardRounded';
import { ADMIN_ROLES, type AdminRole } from '@lions/contracts';

import { type FeatureKind, isFeatureShown } from '../features/feature-badges/constants';

export interface NavTab {
  label: string;
  path: string;
  /** Prompt that builds the screen, shown on its placeholder. */
  builtIn: string;
  /** Narrower than the item's roles (SUPER_ADMIN always passes); the tab is hidden otherwise. */
  roles?: readonly AdminRole[];
  /** v3 feature badge: a static tab disappears when static features are switched off. */
  badge?: FeatureKind;
}

/** Sidebar groups, in order (DS-01, DESIGN-V3); the legacy item order is kept within them. */
export const NAV_GROUPS = [
  { id: 'overview', label: 'Overview' },
  { id: 'manage', label: 'Manage' },
  { id: 'engagement', label: 'Fan engagement' },
  { id: 'administration', label: 'Administration' },
] as const;
export type NavGroupId = (typeof NAV_GROUPS)[number]['id'];

export interface NavItem {
  id:
    | 'dashboard'
    | 'match-centre'
    | 'insights'
    | 'content'
    | 'advertisements'
    | 'teams'
    | 'notifications'
    | 'feed'
    | 'community'
    | 'rewards'
    | 'fan-creations'
    | 'settings';
  group: NavGroupId;
  label: string;
  path: string;
  /** Material Rounded icon (DESIGN-V3). */
  icon: SvgIconComponent;
  /**
   * Roles that see the item and may open its routes; SUPER_ADMIN always may. From the role matrix
   * in 06-security-architecture.md: any access, "View" included, shows the module.
   */
  roles: readonly AdminRole[];
  /** Not in the sidebar (yet). */
  hidden?: boolean;
  /** v3 feature badge: a static page disappears when static features are switched off. */
  badge?: FeatureKind;
  tabs?: readonly NavTab[];
  builtIn: string;
}

export const NAV_ITEMS = [
  {
    id: 'dashboard',
    group: 'overview',
    label: 'Dashboard',
    path: '/dashboard',
    icon: SpaceDashboardRoundedIcon,
    roles: ADMIN_ROLES,
    builtIn: 'P15',
  },
  {
    id: 'match-centre',
    group: 'overview',
    label: 'Match Centre',
    path: '/match-centre',
    icon: SensorsRoundedIcon,
    roles: ADMIN_ROLES,
    badge: 'new',
    builtIn: 'DESIGN-V3',
  },
  {
    id: 'insights',
    group: 'overview',
    label: 'Insights',
    path: '/insights',
    icon: InsightsRoundedIcon,
    roles: ['SUPER_ADMIN', 'MARKETING_MANAGER'],
    badge: 'static',
    builtIn: 'DESIGN-V3',
  },
  {
    id: 'content',
    group: 'manage',
    label: 'Content',
    path: '/content',
    icon: SmartDisplayRoundedIcon,
    // Every Content tab's API is CONTENT_MANAGER only (05 § Admin surface; BACKEND-10 decision).
    roles: ['SUPER_ADMIN', 'CONTENT_MANAGER'],
    builtIn: 'P09',
    tabs: [
      { label: 'Videos', path: '/content/videos', builtIn: 'P09' },
      { label: 'Articles & News', path: '/content/articles', builtIn: 'P09' },
      { label: 'Events', path: '/content/events', builtIn: 'P10' },
      { label: 'Gallery', path: '/content/gallery', builtIn: 'P11' },
    ],
  },
  {
    id: 'advertisements',
    group: 'manage',
    label: 'Advertisement',
    path: '/advertisements',
    icon: CampaignRoundedIcon,
    // `/v1/admin/advertisements` is MARKETING_MANAGER only (05), so CONTENT_MANAGER has no view.
    roles: ['SUPER_ADMIN', 'MARKETING_MANAGER'],
    builtIn: 'P13',
  },
  {
    id: 'teams',
    group: 'manage',
    label: 'Teams & Stats',
    path: '/teams',
    icon: GroupsRoundedIcon,
    roles: ['SUPER_ADMIN', 'CONTENT_MANAGER'],
    builtIn: 'P07',
  },
  {
    id: 'notifications',
    group: 'manage',
    label: 'Notifications',
    path: '/notifications',
    icon: NotificationsActiveRoundedIcon,
    roles: ['SUPER_ADMIN', 'NOTIFICATION_MANAGER', 'APPROVER'],
    builtIn: 'P16',
    tabs: [
      // Concerns and templates are NOTIFICATION_MANAGER only (05); approvers see Campaigns.
      {
        label: 'Concerns',
        path: '/notifications/concerns',
        builtIn: 'P16',
        roles: ['NOTIFICATION_MANAGER'],
      },
      {
        label: 'Templates',
        path: '/notifications/templates',
        builtIn: 'P16',
        roles: ['NOTIFICATION_MANAGER'],
      },
      { label: 'Campaigns', path: '/notifications/bulk', builtIn: 'P17' },
      {
        label: 'Fan preferences',
        path: '/notifications/preferences',
        builtIn: 'DESIGN-V3',
        roles: ['NOTIFICATION_MANAGER'],
        badge: 'static',
      },
    ],
  },
  {
    id: 'feed',
    group: 'engagement',
    label: 'For You Feed',
    path: '/feed',
    icon: AutoAwesomeRoundedIcon,
    roles: ['SUPER_ADMIN', 'CONTENT_MANAGER'],
    badge: 'static',
    builtIn: 'DESIGN-V3',
  },
  {
    id: 'community',
    group: 'engagement',
    label: 'Community',
    path: '/community',
    icon: ForumRoundedIcon,
    roles: ['SUPER_ADMIN', 'CONTENT_MANAGER'],
    builtIn: 'DESIGN-V3',
    tabs: [
      {
        label: 'Moderation',
        path: '/community/moderation',
        builtIn: 'DESIGN-V3',
        badge: 'static',
      },
      { label: 'Polls & Predictions', path: '/community/polls', builtIn: 'FEAT-02', badge: 'new' },
      { label: 'Sharing', path: '/community/sharing', builtIn: 'DESIGN-V3', badge: 'static' },
    ],
  },
  {
    id: 'rewards',
    group: 'engagement',
    label: 'Fan Rewards',
    path: '/rewards',
    icon: LoyaltyRoundedIcon,
    roles: ['SUPER_ADMIN', 'MARKETING_MANAGER'],
    badge: 'static',
    builtIn: 'DESIGN-V3',
    tabs: [
      { label: 'Earning rules', path: '/rewards/rules', builtIn: 'DESIGN-V3' },
      { label: 'Rewards catalogue', path: '/rewards/catalogue', builtIn: 'DESIGN-V3' },
      { label: 'Tiers & CRM', path: '/rewards/tiers', builtIn: 'DESIGN-V3' },
    ],
  },
  {
    id: 'fan-creations',
    group: 'engagement',
    label: 'Fan Creations',
    path: '/fan-creations',
    icon: PhotoCameraRoundedIcon,
    roles: ['SUPER_ADMIN', 'CONTENT_MANAGER'],
    badge: 'static',
    builtIn: 'DESIGN-V3',
  },
  {
    id: 'settings',
    group: 'administration',
    label: 'Settings',
    path: '/settings',
    icon: SettingsRoundedIcon,
    // Stadium is CONTENT_MANAGER (BACKEND-14); Legal Pages and Admin Users stay SUPER_ADMIN.
    roles: ['SUPER_ADMIN', 'CONTENT_MANAGER'],
    builtIn: 'P05',
  },
] as const satisfies readonly NavItem[];

export type NavId = NavItem['id'];

export function navItem(id: NavId): NavItem {
  const item = NAV_ITEMS.find((candidate) => candidate.id === id);
  if (!item) throw new Error(`Unknown nav item ${id}`);
  return item;
}

/** Sidebar entries for an admin, in legacy order; static pages follow the static-features flag. */
export function visibleNavItems(
  hasAnyRole: (roles: readonly AdminRole[]) => boolean,
  items: readonly NavItem[] = NAV_ITEMS,
): NavItem[] {
  return items.filter(
    (item: NavItem) => !item.hidden && isFeatureShown(item.badge) && hasAnyRole(item.roles),
  );
}

/** A section's tabs an admin may open: its roles narrow the item's; static tabs follow the flag. */
export function visibleTabs(
  item: NavItem,
  hasAnyRole: (roles: readonly AdminRole[]) => boolean,
): NavTab[] {
  return (item.tabs ?? []).filter(
    (tab) => isFeatureShown(tab.badge) && (!tab.roles || hasAnyRole(tab.roles)),
  );
}

/** Visible items under their group headings; groups without items are left out. */
export function groupedNavItems(items: readonly NavItem[]) {
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: items.filter((item) => item.group === group.id),
  })).filter((group) => group.items.length > 0);
}

export interface PageEntry {
  label: string;
  path: string;
  /** The module a tab belongs to ("Content" for Videos). */
  section?: string;
  icon: SvgIconComponent;
}

/** Every page an admin may open (modules and the tabs they may see), for the command palette. */
export function pageEntries(
  items: readonly NavItem[],
  hasAnyRole: (roles: readonly AdminRole[]) => boolean,
): PageEntry[] {
  return items.flatMap((item) => [
    { label: item.label, path: item.path, icon: item.icon },
    ...visibleTabs(item, hasAnyRole).map((tab) => ({
      label: tab.label,
      path: tab.path,
      section: item.label,
      icon: item.icon,
    })),
  ]);
}

export interface Crumb {
  label: string;
  /** Without a path the crumb is plain text (a sidebar group: "Overview"). */
  path?: string;
  /** The module's v3 feature badge, shown beside the crumb in the header. */
  badge?: FeatureKind;
}

const within = (pathname: string, path: string) =>
  pathname === path || pathname.startsWith(`${path}/`);

const itemFor = (pathname: string) =>
  NAV_ITEMS.find((candidate: NavItem) => within(pathname, candidate.path)) as NavItem | undefined;

/** Breadcrumb trail for a path: the module, then its tab ("Content › Gallery"). */
export function breadcrumbsFor(pathname: string): Crumb[] {
  const item = itemFor(pathname);
  if (!item) return [];
  const tab = item.tabs?.find((candidate) => within(pathname, candidate.path));
  const trail: Crumb[] = [{ label: item.label, path: item.path, badge: item.badge }];
  if (tab) trail.push({ label: tab.label, path: tab.path });
  return trail;
}

/**
 * The header trail (DESIGN-V3): the module and tab, plus any deeper crumbs a page adds. A page
 * with nothing under its module reads "Group › Page" ("Overview › Dashboard"), the group as text.
 */
export function headerTrail(pathname: string, extra: readonly Crumb[] = []): Crumb[] {
  const trail = [...breadcrumbsFor(pathname), ...extra];
  if (trail.length !== 1) return trail;
  const group = NAV_GROUPS.find((candidate) => candidate.id === itemFor(pathname)?.group);
  return group ? [{ label: group.label }, ...trail] : trail;
}
